import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import { replace } from 'react-router-redux'
import find from 'lodash/find'
import get from 'lodash/get'
import compose from 'lodash/fp/compose'

import env from 'env'
import errorBoundary from 'client/shared/decorators/error-boundary'
import subscriptionHelper from 'client/shared/helpers/subscription-helper'

import {
  analyticsEvent,
  DURATION_SELECTED,
  SUBSCRIPTION_INITIATED,
  PAYMENT_METHOD_SELECTED,
} from 'client/shared/reducers/analytics-reducer'

import {
  haveUserAccessLevelsLoaded,
  hasFeaturedProductLoaded,
  getFeaturedProduct,
  getUserAccessLevels,
  getUserSubscriptions,
} from 'client/bookmate/selectors/subscription-selectors'

import HeadTitle from 'client/shared/blocks/head-title'

import HeaderBox from 'client/shared/boxes/header-box'
import { LayoutColumn } from 'client/shared/blocks/layout-column'
import Copyright from 'client/shared/blocks/copyright'
import { SubscriptionForm } from 'client/bookmate/blocks/subscription-form'
import Spacer from 'client/shared/blocks/spacer'

import { Dispatch, State as ConnectedState } from 'shared/types/redux'
import {
  Product,
  ProductPlan,
  AccessLevels,
  PaymentSystem,
  CurrentUserSubscriptions,
} from 'client/shared/types/subscription'
import {
  PaymentIntent,
  State as StripeState,
} from 'client/shared/reducers/stripe-reducer'
import { CurrentUserState } from 'client/shared/types/current-user'
import { ResourceProps, ResourceName } from 'client/shared/types/resource'

import {
  getResourceByIdGetter,
  getResourceLoader,
} from 'client/shared/helpers/resource-helpers'
import { loadProducts } from '../reducers/subscription-reducer'
import prepareComponent from 'client/shared/decorators/prepare-component'
import { Location } from 'client/shared/types/react-router'
import { RecaptchaNotice } from 'client/shared/blocks/recaptcha/recaptcha-notice'
import { QueryParams } from 'shared/tools/url-helper'

type Props = {
  dispatch: Dispatch
  currentUser: CurrentUserState
  userAccessLevels: AccessLevels
  userSubscriptions: CurrentUserSubscriptions
  product: Product | null | undefined
  stripe: StripeState
  query?: QueryParams
  paymentIntent: PaymentIntent
  haveUserAccessLevelsLoaded: boolean
  hasFeaturedProductLoaded: boolean
  resource: ResourceProps | null | undefined
  products?: Product[]
  offerRegularPlan: boolean
  offerDurationWithTrial?: boolean
  location: Location
} & withI18nProps

type State = {
  currentPlan: ProductPlan | null | undefined
  isDurationWithTrialLoaded?: boolean
  selectedPaymentSystem: PaymentSystem
}

class SubscriptionFeaturedProductBox extends PureComponent<Props, State> {
  state = {
    currentPlan: null,
    isDurationWithTrialLoaded: false,
    selectedPaymentSystem: 'cards' as PaymentSystem,
  }

  static getDerivedStateFromProps(
    nextProps: { product: Product; location: Location },
    prevState: { currentPlan: ProductPlan },
  ): { currentPlan: ProductPlan } | null {
    const { currentPlan } = prevState
    const { product, location } = nextProps

    if (currentPlan || !product) {
      return null
    } else {
      const {
        button: { plans },
      } = product
      const _currentPlan =
        subscriptionHelper.getPreselectedPlan(plans, location.query.duration) ||
        plans[0]
      return { currentPlan: _currentPlan }
    }
  }

  componentDidMount(): void {
    const { dispatch, product } = this.props

    if (!env.isDevelopment() && !product) {
      this.handleUnavailableProduct()
    } else {
      if (!product) return

      dispatch(
        analyticsEvent(SUBSCRIPTION_INITIATED, {
          sub_type: product.id,
          is_trial: get(product, 'trial'),
        }),
      )
    }
  }

  async loadNoTrialProduct(): Promise<void> {
    const { dispatch, offerRegularPlan } = this.props
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
    await dispatch(loadProducts(offerRegularPlan))

    const _currentProduct = this.props.products?.filter(
      _product =>
        subscriptionHelper.isMonthly(_product) &&
        subscriptionHelper.isMasterProduct(_product),
    )[0]

    const _currentPlan =
      _currentProduct &&
      _currentProduct.button.plans.filter((plan: ProductPlan) =>
        subscriptionHelper.isMonthly(plan),
      )[0]

    this.setState({
      currentPlan: _currentPlan ? _currentPlan : this.state.currentPlan,
    })
  }

  async loadProductsInAppPurchase(): Promise<void> {
    const {
      dispatch,
      location: { query },
    } = this.props

    await dispatch(loadProducts(true))

    if (query.duration === 'year') {
      const _currentProduct = this.props.products?.filter(_product =>
        subscriptionHelper.isMasterProduct(_product),
      )[0]

      const _currentPlan =
        _currentProduct &&
        _currentProduct.button.plans.filter((plan: ProductPlan) =>
          subscriptionHelper.isYearly(plan),
        )[0]

      this.setState({
        currentPlan: _currentPlan ? _currentPlan : this.state.currentPlan,
      })
    }
  }

  componentDidUpdate(prevProps: { offerRegularPlan: boolean }): void {
    const { product, offerRegularPlan, offerDurationWithTrial } = this.props

    if (!env.isDevelopment() && !product) {
      return this.handleUnavailableProduct()
    }

    if (offerDurationWithTrial && !this.state.isDurationWithTrialLoaded) {
      this.loadProductsInAppPurchase()
      this.setState({ isDurationWithTrialLoaded: true })
    }

    if (prevProps.offerRegularPlan !== offerRegularPlan && offerRegularPlan) {
      this.loadNoTrialProduct()
    }
  }

  handleUnavailableProduct(): void {
    // someone visited the subscription/featured_product url directly,
    // but we don't have any featured products to offer
    // should redirect the user back to the regular subscription page
    const {
      // eslint-disable-next-line no-shadow
      haveUserAccessLevelsLoaded,
      // eslint-disable-next-line no-shadow
      hasFeaturedProductLoaded,
      userAccessLevels,
      userSubscriptions,
      dispatch,
      query,
    } = this.props
    if (haveUserAccessLevelsLoaded && hasFeaturedProductLoaded) {
      dispatch(
        replace(
          subscriptionHelper.getSubscriptionPagePath(
            userAccessLevels,
            query,
            userSubscriptions.recurrents,
          ),
        ),
      )
    }
  }

  getAllPlans(): ProductPlan[] {
    const { product, products, offerDurationWithTrial } = this.props

    if (offerDurationWithTrial && product) {
      const _products = products?.filter(_product =>
        subscriptionHelper.isMasterProduct(_product),
      )[0]

      const masterYearPlan = _products?.button.plans?.filter(_plan =>
        subscriptionHelper.isYearly(_plan),
      )[0]

      if (!masterYearPlan) return []

      return [...product.button.plans, masterYearPlan]
    }

    return product ? product.button.plans : []
  }

  changeDuration = (duration: string): void => {
    const { currentPlan } = this.state
    if (!currentPlan) return // this should not happen

    const plans = this.getAllPlans()
    const plansWithNewDuration = plans.filter(
      plan => plan.duration === duration,
    )
    const planWithNewDurationAndSameSystem = find(
      plansWithNewDuration,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      plan => plan.system === currentPlan.system,
    )

    const newPlan = planWithNewDurationAndSameSystem
      ? planWithNewDurationAndSameSystem
      : plansWithNewDuration[0]

    this.setState({ currentPlan: newPlan })

    this.props.dispatch(
      analyticsEvent(DURATION_SELECTED, {
        duration: newPlan.duration,
      }),
    )
  }

  changePaymentSystem = (system: string): void => {
    const { currentPlan } = this.state
    const { dispatch } = this.props

    if (!currentPlan) return

    const newPlan = find(
      this.getAllPlans(),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      plan => plan.duration === currentPlan.duration && plan.system === system,
    )

    if (newPlan) {
      this.setState({
        currentPlan: newPlan,
        selectedPaymentSystem: newPlan.system as PaymentSystem,
      })

      dispatch(
        analyticsEvent(PAYMENT_METHOD_SELECTED, {
          payment_method: newPlan.system,
        }),
      )
    }
  }

  render(): JSX.Element | null {
    const {
      currentUser,
      product,
      offerRegularPlan,
      offerDurationWithTrial,
      paymentIntent,
    } = this.props
    const { currentPlan } = this.state

    if (!this.props.product) return null

    const plans = this.getAllPlans()

    return (
      <div className="subscription-featured__wrapper">
        <HeaderBox />
        <LayoutColumn>
          <Spacer size={32} />
          {this.renderMeta()}
          {currentPlan && product && (
            <SubscriptionForm
              user={currentUser}
              plans={plans}
              product={product}
              trial={false}
              currentPlan={currentPlan}
              changeDuration={this.changeDuration}
              changePaymentSystem={this.changePaymentSystem}
              paymentIntent={paymentIntent}
              path="featured"
              offerRegularPlan={offerRegularPlan}
              offerDurationWithTrial={offerDurationWithTrial}
              selectedPaymentSystem={this.state.selectedPaymentSystem}
            />
          )}
          <Spacer size={64} />
          <Copyright centered />
          <RecaptchaNotice />
          <Spacer size="bottom-page" />
        </LayoutColumn>
      </div>
    )
  }

  renderMeta(): JSX.Element | null {
    const { product } = this.props
    if (!product) return null

    const headTitle = product.title

    return (
      <>
        <HeadTitle>{headTitle}</HeadTitle>
      </>
    )
  }
}

const prepareWrapper = prepareComponent(async function ({ store, location }) {
  const {
    query: { resource_uuid, resource_type },
  } = location

  const { dispatch } = store

  const resourceLoader =
    resource_type && getResourceLoader(resource_type as ResourceName)
  if (resource_uuid && resourceLoader) {
    await dispatch(resourceLoader(resource_uuid))
  }
})

type RequestOrigin = 'in_app'

const connectWrapper = connect(
  (
    state: ConnectedState,
    ownProps: {
      location: {
        query: {
          resource_uuid: string
          resource_type: ResourceName
          origin?: RequestOrigin
        }
      }
    },
  ) => {
    const {
      location: {
        query: { resource_uuid, resource_type, origin },
      },
    } = ownProps
    let resource

    const getResourceById =
      resource_type && getResourceByIdGetter(resource_type)

    if (resource_uuid && getResourceById) {
      resource = getResourceById(state, state[resource_type].uuid)
    }
    return {
      currentUser: state.currentUser,
      query: state.app.storedQuery,
      userAccessLevels: getUserAccessLevels(state),
      userSubscriptions: getUserSubscriptions(state),
      product: getFeaturedProduct(state),
      haveUserAccessLevelsLoaded: haveUserAccessLevelsLoaded(state),
      hasFeaturedProductLoaded: hasFeaturedProductLoaded(state),
      products: state.subscription.products,
      resource,
      offerRegularPlan: state.stripe.offerRegularPlan,
      offerDurationWithTrial: origin === 'in_app',
      paymentIntent: state.stripe.paymentIntent,
    }
  },
)

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(SubscriptionFeaturedProductBox)
